import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '@store/authStore';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Stack,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    InputAdornment,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { useAppTheme } from '@contexts/ThemeContext';
import { usePermissions } from '@contexts/PermissionContext';
import {
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    Pending as PendingIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    PictureAsPdf as PDFIcon,
    Download as DownloadIcon,
    Upload as UploadIcon,
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import httpClient from '@utils/httpClient';
import { formatTableDate } from '@utils/dateFormatter';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
    interface jsPDF {
        autoTable: (options: any) => jsPDF;
    }
}

interface Task {
    id: number;
    program_id: number;
    mode: 'Online' | 'In-person' | 'Hybrid' | 'Remote';
    key_deliverable: string;
    tools_resources: string;
    checklist_id: number;
    status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
    assigned_to: string;
    due_date: string;
    completion_date: string;
    notes: string;
    actions: any;
    created_at: string;
    updated_at: string;
    taskName: string;
}

interface WorkshopProgram {
    id: string;
    title: string;
    description: string;
    assignedTo: string;
    dueDate: string;
    status: string;
    program_type: string;
    createdById: string;
    createdByName: string;
    creatorEmail: string;
    creatorRole: string;
    createdAt: string;
    updatedAt: string;
}

const WorkshopTasksPage: React.FC = () => {
    const { programType } = useParams<{ programType: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const { canEditTask } = usePermissions();
    const theme = useTheme();
    const { isDarkMode } = useAppTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const [programCreatorId, setProgramCreatorId] = useState<string>('');

    // Helper function to determine if a task is predefined
    const isPredefinedTask = (checklistId: number | string | null, programType: number = 2) => {
        if (!checklistId) return false;
        const id = typeof checklistId === 'string' ? parseInt(checklistId) : checklistId;
        // For workshops: predefined tasks have checklist_id <= 70
        return id <= 70;
    };

    // Status badge component - ULTRA OPTIMIZED for instant text reflection
    const getStatusBadge = useCallback((status: string) => {
        let config;

        switch (status) {
            case 'completed':
                config = {
                    label: 'Completed',
                    color: '#4caf50',
                    bgColor: isDarkMode ? '#e8f5e8' : '#e8f5e8'
                };
                break;
            case 'in_progress':
                config = {
                    label: 'In Progress',
                    color: '#ff9800',
                    bgColor: isDarkMode ? '#fff3e0' : '#fff3e0'
                };
                break;
            case 'cancelled':
                config = {
                    label: 'Cancelled',
                    color: '#f44336',
                    bgColor: isDarkMode ? '#ffebee' : '#ffebee'
                };
                break;
            default:
                config = {
                    label: 'Pending',
                    color: '#757575',
                    bgColor: isDarkMode ? '#2a2a2a' : '#f5f5f5'
                };
        }

        return (
            <Chip
                label={config.label}
                size="small"
                sx={{
                    backgroundColor: config.bgColor,
                    color: config.color,
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    height: '24px'
                }}
            />
        );
    }, [isDarkMode]);

    // Checkbox styles - Pre-computed for performance
    const checkboxStyles = useMemo(() => ({
        color: theme.palette.text.secondary,
        '&.Mui-checked': { color: '#4caf50' },
        '&.MuiCheckbox-indeterminate': { color: '#ff9800' },
        '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
        }
    }), [isDarkMode, theme]);

    // Consistent Edit/Delete button styles
    const editButtonStyles = useMemo(() => ({
        backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.15)',
        color: '#FF9800',
        width: '36px',
        height: '36px',
        minWidth: '36px',
        minHeight: '36px',
        padding: 0,
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.25)'
        },
        '&.Mui-disabled': {
            backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.15)',
            color: '#FF9800',
            opacity: 0.6
        },
        '&.MuiIconButton-root': {
            backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.15)',
            color: '#FF9800'
        }
    }), [isDarkMode]);

    const deleteButtonStyles = useMemo(() => ({
        backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.15)',
        color: '#F44336',
        width: '36px',
        height: '36px',
        minWidth: '36px',
        minHeight: '36px',
        padding: 0,
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.25)'
        },
        '&.Mui-disabled': {
            backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.15)',
            color: '#F44336',
            opacity: 0.6
        },
        '&.MuiIconButton-root': {
            backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.15)',
            color: '#F44336'
        }
    }), [isDarkMode]);

    // Mobile card component - UNIFORM LAYOUT
    const TaskCard = ({ task }: { task: Task }) => {
        const cardStyles = {
            mb: 2,
            boxShadow: 2,
            borderRadius: 2,
            backgroundColor: isDarkMode
                ? 'rgba(255, 255, 255, 0.05)'
                : theme.palette.background.paper,
            backdropFilter: 'blur(10px)',
            border: isDarkMode
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : `1px solid ${theme.palette.divider}`,
            '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease'
            }
        };

        return (
            <Card sx={cardStyles}>
                <CardContent sx={{ p: 2.5 }}>
                    {/* Header Section - Checkbox + Status + Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Checkbox
                                checked={task.status === 'completed'}
                                indeterminate={task.status === 'in_progress'}
                                onChange={() => handleTaskStatusChange(task.id.toString())}
                                sx={checkboxStyles}
                            />
                            {getStatusBadge(task.status)}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title={isPredefinedTask(task.checklist_id, 2) ? "Cannot edit predefined task" : !canEditTask(task, programCreatorId) ? "No permission to edit" : "Edit Task"}>
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={isPredefinedTask(task.checklist_id, 2) || !canEditTask(task, programCreatorId)}
                                        disableRipple
                                        onClick={() => handleEditTask(task)}
                                        style={{
                                            backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.15)',
                                            color: '#FF9800',
                                            width: '36px',
                                            height: '36px',
                                            minWidth: '36px',
                                            minHeight: '36px',
                                            padding: 0,
                                            borderRadius: '8px'
                                        }}
                                        sx={editButtonStyles}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            {(user?.role !== 'Employee') && (
                                <Tooltip title={isPredefinedTask(task.checklist_id, 2) ? "Cannot delete predefined task" : !canEditTask(task, programCreatorId) ? "No permission to delete" : "Delete Task"}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={isPredefinedTask(task.checklist_id, 2) || !canEditTask(task, programCreatorId)}
                                            disableRipple
                                            onClick={() => handleDeleteTask(task)}
                                            style={{
                                                backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.15)',
                                                color: '#F44336',
                                                width: '36px',
                                                height: '36px',
                                                minWidth: '36px',
                                                minHeight: '36px',
                                                padding: 0,
                                                borderRadius: '8px'
                                            }}
                                            sx={deleteButtonStyles}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>

                    {/* Task Title */}
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold',
                        mb: 1.5,
                        fontSize: '1.1rem',
                        lineHeight: 1.3,
                        color: theme.palette.text.primary
                    }}>
                        {task.taskName}
                    </Typography>

                    {/* Details Section - HORIZONTAL LAYOUT (UNIFORM) */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {/* Status Row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Status:
                            </Typography>
                            {getStatusBadge(task.status)}
                        </Box>

                        {/* Mode Row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Mode:
                            </Typography>
                            <Chip
                                label={task.mode || 'Not specified'}
                                size="small"
                                sx={{
                                    backgroundColor: task.mode === 'Online' ? '#4CAF50' :
                                        task.mode === 'In-person' ? '#FF9800' :
                                            task.mode === 'Hybrid' ? '#9C27B0' : '#2196F3',
                                    color: theme.palette.text.primary,
                                    fontWeight: 'bold'
                                }}
                            />
                        </Box>

                        {/* Assigned Row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Assigned To:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: '#2196F3' }}>
                                    {(task.assigned_to || 'U').charAt(0).toUpperCase()}
                                </Avatar>
                                <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                    {task.assigned_to || 'Unassigned'}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Due Date Row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Due Date:
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                {formatTableDate(task.due_date)}
                            </Typography>
                        </Box>

                        {/* Completion Date Row */}
                        {task.completion_date && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                    Completion Date:
                                </Typography>
                                <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                    {formatTableDate(task.completion_date)}
                                </Typography>
                            </Box>
                        )}

                        {/* Key Deliverable Row */}
                        {task.key_deliverable && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                    Key Deliverable:
                                </Typography>
                                <Typography variant="body2" sx={{ color: theme.palette.text.primary, textAlign: 'right', maxWidth: '60%' }}>
                                    {task.key_deliverable}
                                </Typography>
                            </Box>
                        )}

                        {/* Tools Row */}
                        {task.tools_resources && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                    Tools/Resources:
                                </Typography>
                                <Typography variant="body2" sx={{ color: theme.palette.text.primary, textAlign: 'right', maxWidth: '60%' }}>
                                    {task.tools_resources}
                                </Typography>
                            </Box>
                        )}

                        {/* Notes Row */}
                        {task.notes && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                    Notes:
                                </Typography>
                                <Typography variant="body2" sx={{ color: theme.palette.text.primary, textAlign: 'right', maxWidth: '60%' }}>
                                    {task.notes}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </Card>
        );
    };

    const [tasks, setTasks] = useState<Task[]>([]);
    const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const isFetchingRef = useRef(false);
    const lastFetchedProgramRef = useRef<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [modeFilter, setModeFilter] = useState('');
    const [assignedToFilter, setAssignedToFilter] = useState('');
    const [selectedProgram, setSelectedProgram] = useState('');
    const [currentProgramName, setCurrentProgramName] = useState('');
    const [loadingProgramDetails, setLoadingProgramDetails] = useState(false);
    const [workshopPrograms, setWorkshopPrograms] = useState<WorkshopProgram[]>([]);
    const [loadingPrograms, setLoadingPrograms] = useState(true);
    const [users, setUsers] = useState<Array<{ id: string, name: string, role_name: string }>>([]);
    const [statusColors, setStatusColors] = useState<{ [key: string]: string }>({
        pending: '#000000',
        in_progress: '#FFA500',
        completed: '#008000',
        cancelled: '#FF0000',
        active: '#0066CC'
    });

    // Cache for tasks to prevent unnecessary API calls
    const [tasksCache, setTasksCache] = useState<{ [key: string]: Task[] }>({});
    const [newProgramsBeingCreated, setNewProgramsBeingCreated] = useState<Set<string>>(new Set());

    // Dialog states
    const [editTaskDialogOpen, setEditTaskDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editingTask, setEditingTask] = useState<Task | null>(null);
    const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);

    // Add Checklist Dialog State
    const [openAddDialog, setOpenAddDialog] = useState(false);
    type TaskMode = 'Online' | 'In-person' | 'Hybrid' | 'Remote';
    const [addChecklistLoading, setAddChecklistLoading] = useState<boolean>(false);
    const [addChecklistError, setAddChecklistError] = useState<string>('');

    // React Hook Form for Add Checklist
    const { control: addChecklistControl, handleSubmit: handleAddChecklistSubmit, reset: resetAddChecklistForm, formState: { errors: addChecklistErrors } } = useForm({
        defaultValues: {
            taskName: '',
            toolsResources: '',
            mode: 'Online' as TaskMode,
            keyDeliverable: '',
            assignedTo: '',
            scheduleDate: '',
            completionDate: '',
            notes: ''
        }
    });

    // React Hook Form for Task Form
    const { control: taskControl, handleSubmit: handleTaskSubmit, formState: { errors: taskErrors }, setValue: setTaskValue } = useForm({
        defaultValues: {
            taskName: '',
            assignedTo: '',
            dueDate: '',
            notes: '',
            mode: 'Online' as 'Online' | 'In-person' | 'Hybrid' | 'Remote',
            keyDeliverable: '',
            toolsResources: '',
            completionDate: ''
        }
    });

    // Snackbar states
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    // Program type mapping
    const programTypeMapping: { [key: string]: { title: string; description: string; color: string } } = {
        'planning': {
            title: 'Pre-Event Planning',
            description: 'Initial planning and preparation for training events',
            color: '#2196F3'
        },
        'preparation': {
            title: 'Content & Materials',
            description: 'Development and preparation of training content and materials',
            color: '#4CAF50'
        },
        'logistics': {
            title: 'Logistics & Coordination',
            description: 'Managing logistics, venue arrangements, and coordination activities',
            color: '#FF9800'
        },
        'execution': {
            title: 'Event Day Execution',
            description: 'On-site execution and management of training events',
            color: '#9C27B0'
        },
        'post-session': {
            title: 'Post-Event Follow-up',
            description: 'Post-event activities, feedback collection, and follow-up actions',
            color: '#F44336'
        }
    };


    // Function to fetch program details
    const fetchProgramDetails = useCallback(async (programId: string) => {
        setLoadingProgramDetails(true);
        try {
            const data = await httpClient.get(`/programs/${programId}`);
            if (data.success && data.program) {
                setCurrentProgramName(data.program.title);
                setProgramCreatorId(data.program.created_by_id || '');
            }
        } catch (error) {
            console.error('Error fetching program details:', error);
            setCurrentProgramName('');
            setProgramCreatorId('');
        } finally {
            setLoadingProgramDetails(false);
        }
    }, []);

    const fetchUsers = async () => {
        try {
            console.log('🔄 Fetching users from backend API...');
            const data = await httpClient.get('/users');
            console.log('📊 Users API Response:', data);

            if (data.success && data.users) {
                console.log('👥 Users fetched from API:', data.users);
                setUsers(data.users);
            } else {
                console.log('❌ Users API failed or no data:', data);
            }
        } catch (error) {
            console.error('❌ Error fetching users:', error);
        }
    };

    const fetchStatusColors = async () => {
        try {
            console.log('🎨 Fetching status colors from backend API...');
            const data = await httpClient.get('/programs/status-colors');
            console.log('🎨 Status Colors API Response:', data);

            if (data.success && data.data) {
                console.log('🎨 Status colors fetched from API:', data.data);
                setStatusColors(data.data);
            } else {
                console.log('❌ Status colors API failed or no data:', data);
            }
        } catch (error) {
            console.error('❌ Error fetching status colors:', error);
        }
    };

    const fetchWorkshopTasks = useCallback(async (programId?: string, forceRefresh = false) => {
        // Use provided programId or get from URL parameters, then selectedProgram
        const urlProgramId = searchParams.get('programId');
        const targetProgramId = programId || urlProgramId || selectedProgram;

        console.log('🔍 Fetching tasks for program:', targetProgramId, 'forceRefresh:', forceRefresh);

        // Check if we already fetched tasks for this program (unless forcing refresh)
        if (!forceRefresh && lastFetchedProgramRef.current === targetProgramId) {
            console.log('📦 Tasks already fetched for program:', targetProgramId);
            return;
        }

        // Check if we're already fetching for this program
        if (isFetchingRef.current) {
            console.log('⏳ Already fetching tasks for program:', targetProgramId);
            return;
        }

        // Check if this new program is already being created
        if (newProgramsBeingCreated.has(targetProgramId)) {
            console.log('⏳ New program is already being created:', targetProgramId);
            return;
        }

        // Check cache first
        if (tasksCache[targetProgramId]) {
            console.log('📦 Using cached tasks for program:', targetProgramId);
            const cachedTasks = tasksCache[targetProgramId];
            console.log('📦 Cached tasks count:', cachedTasks.length);

            // Ensure cached tasks are unique
            const uniqueCachedTasks = cachedTasks.filter((task, index, self) =>
                index === self.findIndex(t => t.id === task.id)
            );

            console.log('📦 Unique cached tasks count:', uniqueCachedTasks.length);
            setTasks(uniqueCachedTasks);
            setLoading(false);
            return;
        }

        // Prevent multiple simultaneous API calls
        if (isFetchingRef.current) {
            console.log('⏳ Already fetching tasks, skipping duplicate call');
            return;
        }

        isFetchingRef.current = true;

        // Check if this is a new program (not in cache)
        const isNewProgram = !tasksCache[targetProgramId];
        if (isNewProgram) {
            console.log('🆕 New program detected, marking as being created:', targetProgramId);
            setNewProgramsBeingCreated(prev => new Set(prev).add(targetProgramId));
        }

        try {
            if (!targetProgramId) {
                console.log('❌ No program ID available for fetching tasks');
                setTasks([]);
                setLoading(false);
                return;
            }

            // Fetch program progress data using correct endpoint
            const progressData = await httpClient.get(`/programs/${targetProgramId}/progress`);

            console.log('🔍 Workshop Tasks - Progress API Response:', progressData);
            console.log('🔍 Workshop Tasks - Progress data length:', progressData.data?.length);

            // For new programs, if no tasks found, wait a bit and retry once
            if (progressData.success && (!progressData.data || progressData.data.length === 0)) {
                console.log('🔄 No tasks found for new program, retrying in 1 second...');
                await new Promise(resolve => setTimeout(resolve, 1000));

                const retryData = await httpClient.get(`/programs/${targetProgramId}/progress`);
                console.log('🔍 Workshop Tasks - Retry API Response:', retryData);
                console.log('🔍 Workshop Tasks - Retry data length:', retryData.data?.length);

                if (retryData.success && retryData.data && retryData.data.length > 0) {
                    console.log('✅ Tasks found on retry, using retry data');
                    progressData.data = retryData.data;
                }
            }

            // Convert progress data to Task format
            const tasks: Task[] = [];

            if (progressData.success && progressData.data) {
                progressData.data.forEach((progressItem: any) => {
                    // Convert backend status to frontend format
                    let frontendStatus = progressItem.status || 'pending';
                    if (frontendStatus === 'in-progress') {
                        frontendStatus = 'in_progress';
                    }

                    tasks.push({
                        id: parseInt(progressItem.id),
                        program_id: parseInt(progressItem.programId),
                        mode: progressItem.mode || 'Online',
                        key_deliverable: progressItem.keyDeliverable || progressItem.taskName || 'Task',
                        tools_resources: progressItem.toolsResources || '',
                        checklist_id: parseInt(progressItem.checklistId),
                        status: frontendStatus,
                        assigned_to: progressItem.assignedTo || 'System',
                        due_date: progressItem.dueDate || '',
                        completion_date: progressItem.completionDate || '',
                        notes: progressItem.notes || '',
                        actions: progressItem.actions,
                        created_at: progressItem.createdAt || new Date().toISOString(),
                        updated_at: progressItem.updatedAt || new Date().toISOString(),
                        taskName: progressItem.taskName || progressItem.keyDeliverable || 'Task'
                    });
                });
            }

            console.log('🔍 Workshop Tasks - Converted tasks:', tasks.length);
            console.log('🔍 Workshop Tasks - First task:', tasks[0]);

            // Ensure tasks are unique before setting
            const uniqueTasks = tasks.filter((task, index, self) =>
                index === self.findIndex(t => t.id === task.id)
            );

            console.log('🔍 Workshop Tasks - Unique tasks after deduplication:', uniqueTasks.length);
            setTasks(uniqueTasks);

            // Update cache and track last fetched program
            setTasksCache(prev => ({
                ...prev,
                [targetProgramId]: uniqueTasks
            }));
            lastFetchedProgramRef.current = targetProgramId;

            setLoading(false);

        } catch (error) {
            console.error('❌ Error fetching workshop tasks for program ID:', targetProgramId, error);
            setError('Failed to fetch tasks. Please try again.');
            setTasks([]);
            setLoading(false);
        } finally {
            isFetchingRef.current = false;
            // Remove from new programs being created
            if (isNewProgram) {
                console.log('✅ New program creation completed, removing from tracking:', targetProgramId);
                setNewProgramsBeingCreated(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(targetProgramId);
                    return newSet;
                });
            }
        }
    }, [searchParams, programType]);

    useEffect(() => {
        fetchWorkshopPrograms();
        fetchUsers();
    }, []);

    // Handle program selection from URL parameter with debounce
    useEffect(() => {
        const programIdFromUrl = searchParams.get('programId');
        if (programIdFromUrl && workshopPrograms.length > 0) {
            console.log('🔄 Setting program from URL parameter:', programIdFromUrl);

            // Add debounce to prevent rapid changes
            const timeoutId = setTimeout(() => {
                // Only update if it's different from current selection AND not already processing
                if (programIdFromUrl.toString() !== selectedProgram && !isFetchingRef.current && !newProgramsBeingCreated.has(programIdFromUrl.toString())) {
                    console.log('🔄 Program changed, resetting last fetched ref');
                    lastFetchedProgramRef.current = null;
                    // Clear tasks immediately to prevent showing wrong program data
                    setTasks([]);
                    setFilteredTasks([]);
                    setLoading(true);
                    setSelectedProgram(programIdFromUrl.toString());
                } else if (programIdFromUrl.toString() === selectedProgram) {
                    console.log('🔄 Program already selected, skipping update');
                } else if (isFetchingRef.current) {
                    console.log('🔄 Already fetching, skipping program change');
                } else if (newProgramsBeingCreated.has(programIdFromUrl.toString())) {
                    console.log('🔄 New program is being created, skipping URL change');
                }
            }, 100); // Reduced debounce time for faster response

            return () => clearTimeout(timeoutId);
        }
    }, [searchParams, workshopPrograms, selectedProgram, newProgramsBeingCreated]); // Add dependencies back

    // Single useEffect to handle task fetching - prevents duplicate API calls
    useEffect(() => {
        if (selectedProgram && workshopPrograms.length > 0) {
            console.log('🔄 WorkshopTasksPage - Fetching tasks for program:', selectedProgram);

            // Only fetch if we haven't already fetched for this program AND not currently fetching
            if (lastFetchedProgramRef.current !== selectedProgram && !isFetchingRef.current) {
                console.log('🔄 Starting task fetch for program:', selectedProgram);

                // For new programs, add longer delay to allow backend task creation
                const isNewProgram = !tasksCache[selectedProgram];
                const delay = isNewProgram ? 500 : 100; // Longer delay for new programs

                const timeoutId = setTimeout(() => {
                    // Double-check that we're still fetching for the same program
                    if (lastFetchedProgramRef.current !== null && lastFetchedProgramRef.current !== selectedProgram) {
                        console.log('🔄 Program changed during delay, skipping fetch');
                        return;
                    }
                    setLoading(true);
                    setTasks([]); // Clear existing tasks first
                    fetchWorkshopTasks(selectedProgram);
                }, delay);

                return () => clearTimeout(timeoutId);
            } else if (lastFetchedProgramRef.current === selectedProgram) {
                console.log('🔄 Tasks already fetched for program:', selectedProgram);
                // If tasks are already loaded, make sure they're displayed
                if (tasks.length === 0 && tasksCache[selectedProgram]) {
                    console.log('🔄 Restoring tasks from cache for program:', selectedProgram);
                    setTasks(tasksCache[selectedProgram]);
                    setLoading(false);
                }
            } else if (isFetchingRef.current) {
                console.log('🔄 Already fetching tasks, skipping');
            }
        }
    }, [selectedProgram, tasksCache, tasks.length]); // Add dependencies for cache and tasks

    const fetchWorkshopPrograms = async () => {
        try {
            const data = await httpClient.get('/programs/workshops');

            if (data.success && data.programs) {
                const programs: WorkshopProgram[] = data.programs.map((program: any) => ({
                    id: program.id,
                    title: program.title,
                    description: program.description,
                    assignedTo: program.assignedTo,
                    dueDate: program.dueDate,
                    status: program.status,
                    program_type: program.program_type || 'workshop',
                    createdById: program.createdById,
                    createdByName: program.createdByName,
                    creatorEmail: program.creatorEmail,
                    creatorRole: program.creatorRole,
                    createdAt: program.createdAt,
                    updatedAt: program.updatedAt
                }));

                // Define predefined programs order (correct order we want to show)
                const predefinedOrder = [
                    'Pre-Event Planning',
                    'Content & Materials',
                    'Logistics & Coordination',
                    'Event Day Execution',
                    'Post-Event Follow-up'
                ];

                // Sort programs: predefined first (in correct order), then others by creation date
                const sortedPrograms = programs.sort((a, b) => {
                    const aIsPredefined = predefinedOrder.includes(a.title);
                    const bIsPredefined = predefinedOrder.includes(b.title);

                    if (aIsPredefined && bIsPredefined) {
                        // Both are predefined, sort by predefined order
                        return predefinedOrder.indexOf(a.title) - predefinedOrder.indexOf(b.title);
                    } else if (aIsPredefined && !bIsPredefined) {
                        // a is predefined, b is not - a comes first
                        return -1;
                    } else if (!aIsPredefined && bIsPredefined) {
                        // b is predefined, a is not - b comes first
                        return 1;
                    } else {
                        // Both are not predefined, sort by creation date (newest first)
                        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                    }
                });

                setWorkshopPrograms(sortedPrograms);

                // Set first program as selected by default
                if (sortedPrograms.length > 0 && !selectedProgram) {
                    setSelectedProgram(sortedPrograms[0].id.toString());
                }
            }

            setLoadingPrograms(false);
        } catch (error) {
            console.error('❌ Error fetching workshop programs:', error);
            setLoadingPrograms(false);
        }
    };

    // useEffect hooks to control data fetching
    useEffect(() => {
        console.log('🚀 WorkshopTasksPage mounted - initializing data');
        fetchUsers();
        fetchStatusColors();
        fetchWorkshopPrograms();
    }, []); // Only run once on mount

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const programIdFromUrl = urlParams.get('program');

        if (programIdFromUrl) {
            console.log('🔄 Setting program from URL parameter:', programIdFromUrl);
            setSelectedProgram(programIdFromUrl);
        }
    }, []); // Only run once on mount

    useEffect(() => {
        if (selectedProgram) {
            console.log('🔍 Fetching tasks for program:', selectedProgram);
            fetchWorkshopTasks(selectedProgram);
            fetchProgramDetails(selectedProgram);
        }
    }, [selectedProgram, fetchProgramDetails]); // Run when selectedProgram changes

    useEffect(() => {
        console.log('🔍 Workshop Tasks - Filtering tasks. Total tasks:', tasks.length);
        console.log('🔍 Workshop Tasks - Selected program:', selectedProgram);

        // Skip filtering if tasks are empty or loading
        if (tasks.length === 0 || loading) {
            console.log('🔍 Workshop Tasks - Skipping filter (empty tasks or loading)');
            setFilteredTasks([]);
            return;
        }

        // Ensure tasks belong to the selected program
        const tasksForSelectedProgram = tasks.filter(task => task.program_id.toString() === selectedProgram);
        if (tasksForSelectedProgram.length !== tasks.length) {
            console.log('🔍 Workshop Tasks - Filtering out tasks from wrong program');
            console.log('🔍 Workshop Tasks - Tasks for selected program:', tasksForSelectedProgram.length);
            setTasks(tasksForSelectedProgram);
            return;
        }

        let filtered = [...tasks]; // Create a copy to avoid mutating original array

        if (statusFilter) {
            filtered = filtered.filter(task => task.status === statusFilter);
        }

        if (assignedToFilter) {
            filtered = filtered.filter(task => task.assigned_to === assignedToFilter);
        }

        if (searchTerm) {
            filtered = filtered.filter(task =>
                task.taskName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                task.key_deliverable?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                task.tools_resources?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Remove duplicates based on task ID
        const uniqueFiltered = filtered.filter((task, index, self) =>
            index === self.findIndex(t => t.id === task.id)
        );

        console.log('🔍 Workshop Tasks - Filtered tasks:', uniqueFiltered.length);
        setFilteredTasks(uniqueFiltered);
    }, [tasks, statusFilter, assignedToFilter, searchTerm]); // Run when filters change

    const handleExportPDF = () => {
        try {
            console.log('📄 Exporting tasks as PDF...');
            console.log('📄 Current tasks state:', tasks);
            const selectedProgramTitle = workshopPrograms.find(p => p.id.toString() === selectedProgram)?.title || 'Unknown Program';

            // Create new PDF document
            const doc = new jsPDF('l', 'mm', 'a4'); // landscape orientation

            // Add title
            doc.setFontSize(20);
            doc.text('Workshop Tasks Report', 14, 22);

            // Program title
            doc.setFontSize(12);
            doc.text(`Program: ${selectedProgramTitle}`, 14, 32);

            // Add generation date and total count
            doc.setFontSize(10);
            doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 40);
            doc.text(`Total Tasks: ${tasks.length}`, 14, 46);

            // Prepare table data
            console.log('PDF Export - Tasks data:', tasks);
            console.log('PDF Export - First task:', tasks[0]);
            console.log('PDF Export - First task status:', tasks[0]?.status);

            const tableData = tasks.map(task => {
                // Format status with visual indicators like table
                let statusText = 'Pending';
                if (task.status === 'completed') {
                    statusText = 'Completed';
                } else if (task.status === 'in_progress') {
                    statusText = 'In Progress';
                } else if (task.status === 'pending') {
                    statusText = 'Pending';
                } else if (task.status === 'cancelled') {
                    statusText = 'Cancelled';
                }

                console.log('PDF Export - Task status mapping:', {
                    taskName: task.taskName,
                    status: task.status,
                    statusText: statusText
                });

                const rowData = [
                    statusText,
                    task.taskName || 'N/A',
                    task.tools_resources || 'N/A',
                    task.mode || 'N/A',
                    task.key_deliverable || 'N/A',
                    task.assigned_to || 'Unassigned',
                    formatTableDate(task.due_date),
                    formatTableDate(task.completion_date),
                    formatTableDate(task.created_at),
                    formatTableDate(task.updated_at),
                    task.notes || 'N/A'
                ];

                console.log('PDF Export - Row data:', rowData);
                console.log('PDF Export - Status text in row:', rowData[0]);

                return rowData;
            });

            // Table columns
            const columns = ['Status', 'Task Name', 'Tools/Resources', 'Mode', 'Key Deliverable', 'Assigned To', 'Schedule Date', 'Completion Date', 'Created At', 'Updated At', 'Notes'];

            console.log('PDF Export - Columns:', columns);
            console.log('PDF Export - Columns length:', columns.length);
            console.log('PDF Export - Table data sample:', tableData.slice(0, 2));
            console.log('PDF Export - Table data length:', tableData.length);
            console.log('PDF Export - First row length:', tableData[0]?.length);

            // Generate table
            autoTable(doc, {
                head: [columns],
                body: tableData,
                startY: 58,
                styles: {
                    fontSize: 7,
                    cellPadding: 3,
                    overflow: 'linebreak',
                    halign: 'left'
                },
                headStyles: {
                    fillColor: [41, 128, 185],
                    fontSize: 8,
                    fontStyle: 'bold',
                    halign: 'center'
                },
                alternateRowStyles: { fillColor: [245, 245, 245] },
                columnStyles: {
                    0: { cellWidth: 20, halign: 'left', fontStyle: 'bold', fontSize: 8 }, // Status - Left aligned, Bold, Larger font
                    1: { cellWidth: 35 }, // Task Name
                    2: { cellWidth: 30 }, // Tools/Resources
                    3: { cellWidth: 15 }, // Mode
                    4: { cellWidth: 30 }, // Key Deliverable
                    5: { cellWidth: 20 }, // Assigned To
                    6: { cellWidth: 20 }, // Schedule Date
                    7: { cellWidth: 20 }, // Completion Date
                    8: { cellWidth: 20 }, // Created At
                    9: { cellWidth: 20 }, // Updated At
                    10: { cellWidth: 25 } // Notes
                },
                willDrawCell: (data: any) => {
                    // Apply colors to Status column (column 0) before drawing
                    if (data.column.index === 0) {
                        console.log('PDF willDrawCell - Full data object:', data);
                        console.log('PDF willDrawCell - Cell object:', data.cell);
                        console.log('PDF willDrawCell - Text array:', data.cell.text);

                        // Get the full text from the cell
                        let statusText = '';
                        if (Array.isArray(data.cell.text)) {
                            statusText = data.cell.text.join('');
                        } else if (typeof data.cell.text === 'string') {
                            statusText = data.cell.text;
                        } else if (data.cell.text && data.cell.text[0]) {
                            statusText = data.cell.text[0];
                        }

                        console.log('PDF willDrawCell - Final status text:', statusText);

                        // Apply colors based on status text
                        if (statusText && statusText.includes('Completed')) {
                            console.log('PDF willDrawCell - Found Completed, applying green');
                            doc.setTextColor(0, 128, 0); // Green
                        } else if (statusText && (statusText.includes('In Progress') || statusText.includes('InProgress'))) {
                            console.log('PDF willDrawCell - Found In Progress, applying orange');
                            doc.setTextColor(255, 140, 0); // Orange
                        } else if (statusText && statusText.includes('Pending')) {
                            console.log('PDF willDrawCell - Found Pending, applying black');
                            doc.setTextColor(0, 0, 0); // Black
                        } else if (statusText && statusText.includes('Cancelled')) {
                            console.log('PDF willDrawCell - Found Cancelled, applying red');
                            doc.setTextColor(255, 0, 0); // Red
                        } else {
                            console.log('PDF willDrawCell - No match found, using default black');
                            doc.setTextColor(0, 0, 0); // Default black
                        }
                    }
                },
                margin: { left: 14, right: 14 }
            });

            // Save the PDF
            doc.save(`${selectedProgramTitle}_tasks_${new Date().toISOString().split('T')[0]}.pdf`);
            console.log('✅ PDF export completed');
        } catch (error) {
            console.error('❌ Error exporting PDF:', error);
            alert('Failed to export PDF. Please try again.');
        }
    };

    const handleExportCSV = () => {
        try {
            console.log('📊 Exporting tasks as CSV...');
            const selectedProgramTitle = workshopPrograms.find(p => p.id.toString() === selectedProgram)?.title || 'Unknown Program';

            // Format dates with right alignment padding
            const formatDateRight = (dateStr: string | null | undefined) => {
                if (!dateStr) return '     '; // 5 spaces for right alignment
                const formattedDate = formatTableDate(dateStr);
                return formattedDate.padStart(15, ' '); // Right align with 15 character width
            };

            const csvData = tasks.map(task => ({
                'Task Name': task.taskName || '',
                'Assigned To': task.assigned_to || 'Unassigned',
                '     Due Date': formatDateRight(task.due_date),
                'Status': task.status || 'pending',
                'Mode': task.mode || '',
                'Key Deliverable': task.key_deliverable || '',
                'Tools/Resources': task.tools_resources || '',
                'Notes': task.notes || '',
                'Completion Date': formatTableDate(task.completion_date),
                'Created At': formatTableDate(task.created_at),
                'Updated At': formatTableDate(task.updated_at)
            }));


            const csvContent = [
                Object.keys(csvData[0]).join(','),
                ...csvData.map(row => Object.values(row).map(value => `"${value}"`).join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `${selectedProgramTitle}_tasks_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            console.log('✅ CSV export completed');
        } catch (error) {
            console.error('❌ Error exporting CSV:', error);
            alert('Failed to export CSV. Please try again.');
        }
    };


    const handleImportTasks = () => {
        try {
            console.log('📥 Opening file picker for task import...');
            // Create a file input element
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.csv,.xlsx,.xls';
            input.style.display = 'none';

            input.onchange = (event) => {
                const file = (event.target as HTMLInputElement).files?.[0];
                if (file) {
                    console.log('📁 File selected:', file.name);
                    // For now, just show a message - full import logic can be implemented later
                    alert(`File "${file.name}" selected. Import functionality will be implemented soon!`);
                }
            };

            // Trigger file picker
            document.body.appendChild(input);
            input.click();
            document.body.removeChild(input);
        } catch (error) {
            console.error('❌ Error opening file picker:', error);
            alert('Failed to open file picker. Please try again.');
        }
    };

    const handleTaskStatusChange = (taskId: string) => {
        // Get current task from filteredTasks (faster than searching all tasks)
        const currentTask = filteredTasks.find(t => t.id.toString() === taskId);
        if (!currentTask) return;

        // Calculate new status
        const nextStatus: 'pending' | 'in_progress' | 'completed' =
            currentTask.status === 'pending' ? 'in_progress' :
                currentTask.status === 'in_progress' ? 'completed' : 'pending';

        const completionDate = nextStatus === 'completed'
            ? (() => {
                const t = new Date();
                return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
            })()
            : nextStatus === 'pending' ? '' : currentTask.completion_date;

        // Store for rollback
        const prevStatus = currentTask.status;
        const prevDate = currentTask.completion_date;

        // ULTRA FAST UPDATE - Single flushSync with optimized updates
        flushSync(() => {
            const updateTask = (task: Task) =>
                task.id.toString() === taskId
                    ? { ...task, status: nextStatus, completion_date: completionDate }
                    : task;

            setTasks(prev => prev.map(updateTask));
            setFilteredTasks(prev => prev.map(updateTask));
        });

        // BACKGROUND API CALL - Non-blocking (immediate async call)
        (async () => {
            try {
                const programId = currentTask.program_id;
                const backendStatus = nextStatus === 'in_progress' ? 'in-progress' : nextStatus;

                await httpClient.put(`/programs/${programId}/progress/${taskId}`, {
                    status: backendStatus,
                    completionDate: nextStatus === 'completed' ? completionDate : '',
                    assignedTo: currentTask.assigned_to,
                    dueDate: currentTask.due_date,
                    notes: currentTask.notes,
                    actions: currentTask.actions,
                    mode: currentTask.mode,
                    keyDeliverable: currentTask.key_deliverable,
                    toolsResources: currentTask.tools_resources
                });

                // Activity logging is handled by the backend API call
                // No need to log separately here to avoid duplicates
            } catch (error) {
                console.error('Error updating task status:', error);
                // Revert on error
                flushSync(() => {
                    const revertTask = (task: Task) =>
                        task.id.toString() === taskId
                            ? { ...task, status: prevStatus, completion_date: prevDate }
                            : task;

                    setTasks(prev => prev.map(revertTask));
                    setFilteredTasks(prev => prev.map(revertTask));
                });
            }
        })();
    };

    const getProgramStatus = () => {
        const totalTasks = tasks.length;
        const completedTasks = tasks.filter(task => task.status === 'completed').length;
        const inProgressTasks = tasks.filter(task => task.status === 'in_progress').length;


        if (completedTasks === totalTasks && totalTasks > 0) {
            return 'completed';
        } else if (completedTasks > 0 || inProgressTasks > 0) {
            return 'in_progress';
        } else {
            return 'pending';
        }
    };

    const handleEditTask = (task: Task) => {
        console.log('✏️ Opening edit dialog for task:', task.id);
        setEditingTask(task);

        // Convert email to name for assignedTo field
        let assignedToValue = task.assigned_to || '';
        if (assignedToValue && assignedToValue.includes('@')) {
            // If it's an email, try to find the corresponding name by matching the name part
            const emailName = assignedToValue.split('@')[0];
            const user = users.find(u => u.name.toLowerCase() === emailName.toLowerCase());
            if (user) {
                assignedToValue = user.name;
            } else {
                // If no user found, just use the name part from email
                assignedToValue = emailName;
            }
        }

        // Set form values using React Hook Form
        setTaskValue('taskName', task.taskName || '');
        setTaskValue('assignedTo', assignedToValue);
        setTaskValue('dueDate', task.due_date || '');
        setTaskValue('notes', task.notes || '');
        setTaskValue('mode', (task.mode as 'Online' | 'In-person' | 'Hybrid' | 'Remote') || 'Online');
        setTaskValue('keyDeliverable', task.key_deliverable || '');
        setTaskValue('toolsResources', task.tools_resources || '');
        setTaskValue('completionDate', task.completion_date || '');

        setEditTaskDialogOpen(true);
    };

    const handleUpdateTask = async (data: any) => {
        console.log('🚀 Workshop handleUpdateTask called with data:', data);
        console.log('🚀 Workshop editingTask:', editingTask);
        console.log('🚀 Workshop selectedProgram:', selectedProgram);

        if (!editingTask || !selectedProgram) {
            console.log('❌ Missing data:', { editingTask: !!editingTask, selectedProgram });
            return;
        }

        try {
            console.log('💾 Updating task:', editingTask.id);
            console.log('📝 Selected Program:', selectedProgram);
            console.log('📝 Task Form Data:', data);

            const updateData = {
                taskName: data.taskName,
                assignedTo: data.assignedTo,
                dueDate: data.dueDate,
                notes: data.notes,
                mode: data.mode,
                keyDeliverable: data.keyDeliverable,
                toolsResources: data.toolsResources,
                completionDate: data.completionDate
            };

            console.log('📤 Update Data to send:', updateData);
            console.log('🌐 API URL:', `http://localhost:5000/api/programs/${selectedProgram}/progress/${editingTask.id}`);

            const responseData = await httpClient.put(`/programs/${selectedProgram}/progress/${editingTask.id}`, updateData);

            console.log('✅ Task updated successfully:', responseData);

            // Clear cache and force fresh fetch
            lastFetchedProgramRef.current = null;
            setTasksCache(prev => {
                const newCache = { ...prev };
                delete newCache[selectedProgram];
                console.log('🗑️ Cleared cache for program:', selectedProgram);
                return newCache;
            });

            // Force fresh fetch by clearing the fetching ref
            isFetchingRef.current = false;
            console.log('🗑️ Cleared fetching ref for program:', selectedProgram);

            await fetchWorkshopTasks(selectedProgram, true);
            setEditTaskDialogOpen(false);
            setEditingTask(null);
            setSnackbarMessage('Task updated successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('❌ Error updating task:', error);
            setSnackbarMessage('An error occurred while updating the task.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleDeleteTask = (task: Task) => {
        console.log('🗑️ Opening delete dialog for task:', task.id);
        setTaskToDelete(task);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!taskToDelete || !selectedProgram) return;

        try {
            console.log('🗑️ Deleting task:', taskToDelete.id);

            await httpClient.delete(`/programs/${selectedProgram}/progress/${taskToDelete.id}`);

            console.log('✅ Task deleted successfully');

            // Optimistically remove from current state
            setTasks(prev => prev.filter(t => t.id !== taskToDelete.id));

            // Clear cache and force fresh fetch
            lastFetchedProgramRef.current = null;
            setTasksCache(prev => {
                const newCache = { ...prev } as any;
                delete newCache[selectedProgram];
                return newCache;
            });

            // Fetch latest from server
            await fetchWorkshopTasks();
            setDeleteDialogOpen(false);
            setTaskToDelete(null);
            setSnackbarMessage('Task deleted successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('❌ Error deleting task:', error);
            setSnackbarMessage('An error occurred while deleting the task.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    // Add Checklist Dialog handlers
    const handleAddDialogClose = () => {
        setOpenAddDialog(false);
        resetAddChecklistForm();
        setAddChecklistError('');
        setAddChecklistLoading(false);
    };

    const handleAddChecklist = async (data: any) => {
        try {
            setAddChecklistLoading(true);
            setAddChecklistError('');

            console.log('📝 Creating new task with data:', {
                taskName: data.taskName,
                mode: data.mode,
                assignedTo: data.assignedTo,
                dueDate: data.scheduleDate
            });

            const requestData = {
                taskName: data.taskName,
                status: 'pending',
                mode: data.mode,
                assignedTo: data.assignedTo || '',
                dueDate: data.scheduleDate || '',
                completionDate: data.completionDate || '',
                notes: data.notes || '',
                toolsResources: data.toolsResources || '',
                keyDeliverable: data.keyDeliverable || ''
            };

            console.log('📤 Sending request to:', `/programs/${selectedProgram}/progress`);
            console.log('📤 Request data:', requestData);

            // Create progress record directly
            const response = await httpClient.post(`/programs/${selectedProgram}/progress`, requestData);

            console.log('✅ Task created successfully:', response);

            setAddChecklistLoading(false);
            handleAddDialogClose();

            // Clear cache and force fresh fetch
            lastFetchedProgramRef.current = null;
            setTasksCache(prev => {
                const newCache = { ...prev };
                delete newCache[selectedProgram];
                return newCache;
            });

            // Refresh the entire list to get the latest data
            await fetchWorkshopTasks();

            setSnackbarMessage('Task added successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

        } catch (error: any) {
            console.error('❌ Error adding checklist:', error);
            console.error('❌ Error response:', error?.response?.data);
            console.error('❌ Error status:', error?.response?.status);
            setAddChecklistLoading(false);
            setAddChecklistError(error?.response?.data?.error || 'Failed to add checklist. Please try again.');
        }
    };


    // Filter tasks based on search and filters - handled by useEffect

    // useEffect hooks
    useEffect(() => {
        fetchStatusColors();
        fetchUsers();
    }, []);

    useEffect(() => {
        fetchWorkshopPrograms();
    }, []);

    useEffect(() => {
        const urlProgramId = searchParams.get('programId');
        if (urlProgramId) {
            setSelectedProgram(urlProgramId);
            fetchWorkshopTasks(urlProgramId);
        }
    }, [searchParams, fetchWorkshopTasks]);


    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    const completedTasks = tasks.filter(task => task.status === 'completed').length;
    const totalTasks = tasks.length;

    return (
        <Box sx={{
            p: { xs: 2, sm: 3 },
            backgroundColor: isDarkMode ? '#1a1a2e' : theme.palette.background.default,
            borderRadius: '8px',
            margin: { xs: '8px', sm: '16px' },
            minHeight: 'calc(100vh - 64px)'
        }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: { xs: 2, sm: 3 },
                gap: { xs: 2, sm: 0 }
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            color: theme.palette.text.primary,
                            fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.25rem' },
                            fontWeight: { xs: 600, sm: 700 },
                            lineHeight: { xs: 1.2, sm: 1.3 }
                        }}
                    >
                        {loadingProgramDetails ? 'Loading...' : (currentProgramName || 'Workshop Tasks')}
                    </Typography>
                    {loadingProgramDetails && (
                        <CircularProgress
                            size={24}
                            sx={{
                                color: theme.palette.primary.main
                            }}
                        />
                    )}
                </Box>
            </Box>

            {/* Program Selector */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 2 },
                mb: { xs: 2, sm: 3 },
                alignItems: { xs: 'stretch', sm: 'center' }
            }}>
                <FormControl sx={{
                    flexGrow: 1,
                    minWidth: { xs: '100%', sm: '200px' },
                    width: { xs: '100%', sm: 'auto' }
                }}>
                    <InputLabel
                        shrink={true}
                        sx={{
                            color: theme.palette.text.primary,
                            '&.Mui-focused': {
                                color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.primary.main,
                            },
                            '&.MuiInputLabel-shrink': {
                                color: theme.palette.text.primary,
                            }
                        }}
                    >
                        Select Program
                    </InputLabel>
                    <Select
                        value={selectedProgram}
                        onChange={(e) => {
                            const newProgramId = e.target.value;
                            console.log('🔄 Program selection changed to:', newProgramId);

                            // Clear existing tasks and set loading immediately
                            setTasks([]);
                            setFilteredTasks([]);
                            setLoading(true);

                            // Reset the last fetched ref to force a new fetch
                            lastFetchedProgramRef.current = null;

                            // Update selected program
                            setSelectedProgram(newProgramId);

                            // fetchWorkshopTasks will be called automatically by useEffect when selectedProgram changes
                        }}
                        disabled={loadingPrograms}
                        sx={{
                            color: theme.palette.text.primary,
                            '& .MuiOutlinedInput-root': {
                                color: theme.palette.text.primary,
                                '& fieldset': {
                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                                },
                                '&:hover fieldset': {
                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.primary.main,
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: isDarkMode ? '#ffffff !important' : theme.palette.primary.main,
                                },
                                '&.Mui-focused.MuiOutlinedInput-root fieldset': {
                                    borderColor: isDarkMode ? '#ffffff !important' : theme.palette.primary.main,
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: theme.palette.text.primary,
                                '&.Mui-focused': {
                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                                },
                                '&.MuiInputLabel-shrink': {
                                    color: theme.palette.text.primary,
                                }
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                '& legend': {
                                    color: theme.palette.text.primary,
                                },
                                '&.Mui-focused': {
                                    borderColor: isDarkMode ? '#ffffff !important' : theme.palette.primary.main,
                                },
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: isDarkMode ? '#ffffff !important' : theme.palette.primary.main,
                            },
                            '& .MuiSelect-icon': {
                                color: theme.palette.text.primary,
                            },
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    backgroundColor: isDarkMode ? '#1a1a2e' : theme.palette.background.paper,
                                    '& .MuiMenuItem-root': {
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.palette.action.focus,
                                        },
                                    },
                                },
                            },
                        }}
                    >
                        {loadingPrograms ? (
                            <MenuItem value="" disabled>
                                Loading programs...
                            </MenuItem>
                        ) : (
                            workshopPrograms.map((program) => (
                                <MenuItem key={program.id} value={program.id.toString()}>
                                    {program.title}
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>
            </Box>

            {/* Summary Cards */}
            <Box
                sx={{
                    display: 'flex',
                    gap: { xs: 1.5, sm: 2 },
                    mb: { xs: 2, sm: 3 },
                    flexWrap: 'wrap',
                    flexDirection: { xs: 'column', sm: 'row' },
                    '& > *': {
                        flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 16px)' },
                        minWidth: { xs: '100%', sm: '200px' }
                    }
                }}
            >
                <Card sx={{
                    minWidth: { xs: '100%', sm: 300 },
                    background: isDarkMode ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.2) 0%, rgba(33, 150, 243, 0.1) 100%)' : `linear-gradient(135deg, ${theme.palette.primary.light}20 0%, ${theme.palette.primary.light}10 100%)`,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(33, 150, 243, 0.3)',
                    borderRadius: { xs: 2, sm: 3 },
                    overflow: 'hidden',
                    position: 'relative',
                    minHeight: { xs: '80px', sm: '120px' },
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(33, 150, 243, 0.3)',
                    },
                    '&:active': {
                        transform: 'translateY(0px)',
                        transition: 'transform 0.1s ease',
                    },
                    // Touch-friendly interactions
                    '@media (hover: none) and (pointer: coarse)': {
                        '&:hover': {
                            transform: 'none',
                        },
                        '&:active': {
                            transform: 'scale(0.98)',
                            transition: 'transform 0.1s ease',
                        },
                    },
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            mb: 1,
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            lineHeight: 1.2
                        }}>
                            Total Tasks
                        </Typography>
                        <Typography variant="h3" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 'bold',
                            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                            textShadow: '0 2px 4px rgba(33, 150, 243, 0.3)',
                            lineHeight: 1.1
                        }}>
                            {totalTasks}
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{
                    minWidth: { xs: '100%', sm: 300 },
                    background: isDarkMode ? 'linear-gradient(135deg, rgba(255, 152, 0, 0.2) 0%, rgba(255, 152, 0, 0.1) 100%)' : 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 152, 0, 0.05) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: isDarkMode ? '1px solid rgba(255, 152, 0, 0.3)' : '1px solid rgba(255, 152, 0, 0.2)',
                    borderRadius: { xs: 2, sm: 3 },
                    overflow: 'hidden',
                    position: 'relative',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(255, 152, 0, 0.3)',
                    },
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            mb: 1,
                            fontSize: '1rem'
                        }}>
                            Active Tasks
                        </Typography>
                        <Typography variant="h3" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 'bold',
                            fontSize: { xs: '2rem', sm: '2.5rem' },
                            textShadow: '0 2px 4px rgba(255, 152, 0, 0.3)'
                        }}>
                            {tasks.filter(t => t.status === 'in_progress').length}
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{
                    minWidth: { xs: '100%', sm: 300 },
                    background: isDarkMode ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(76, 175, 80, 0.1) 100%)' : 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(76, 175, 80, 0.3)',
                    borderRadius: { xs: 2, sm: 3 },
                    overflow: 'hidden',
                    position: 'relative',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(76, 175, 80, 0.3)',
                    },
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            mb: 1,
                            fontSize: '1rem'
                        }}>
                            Completed Tasks
                        </Typography>
                        <Typography variant="h3" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 'bold',
                            fontSize: { xs: '2rem', sm: '2.5rem' },
                            textShadow: '0 2px 4px rgba(76, 175, 80, 0.3)'
                        }}>
                            {completedTasks}
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Search and Filters */}
            <Box
                sx={{
                    display: 'flex',
                    gap: { xs: 1, sm: 2 },
                    mb: { xs: 2, sm: 3 },
                    alignItems: { xs: 'stretch', lg: 'center' },
                    flexDirection: { xs: 'column', lg: 'row' },
                    width: '100%',
                    flexWrap: { xs: 'wrap', lg: 'nowrap' },
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: '8px',
                    padding: { xs: '12px', sm: '16px' },
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : `1px solid ${theme.palette.divider}`
                }}
            >
                <TextField
                    label="Search tasks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        flex: { xs: 'none', lg: '2' },
                        width: { xs: '100%', lg: 'auto' },
                        minWidth: { xs: 'auto', lg: '200px' },
                        '& .MuiOutlinedInput-root': {
                            color: theme.palette.text.primary,
                            '& fieldset': {
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                            },
                            '&:hover fieldset': {
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.primary.main,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: theme.palette.text.primary,
                            '&.Mui-focused': {
                                color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.primary.main,
                            },
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            '& legend': {
                                color: theme.palette.text.primary,
                            },
                            '&.Mui-focused': {
                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                            },
                        },
                    }}
                />

                {/* Filter Controls Container */}
                <Box sx={{
                    display: 'flex',
                    gap: { xs: 1, sm: 1.5, md: 2 },
                    width: { xs: '100%', lg: 'auto' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'stretch',
                    flexWrap: { xs: 'nowrap', sm: 'wrap', md: 'nowrap' }
                }}>
                    <FormControl sx={{
                        minWidth: { xs: 'auto', sm: '100px', md: '120px', lg: '120px' },
                        flex: { xs: 'none', sm: '1', md: '1', lg: '1' },
                        width: { xs: '100%', sm: 'auto' }
                    }}>
                        <InputLabel
                            shrink={true}
                            sx={{
                                color: theme.palette.text.primary,
                                '&.Mui-focused': {
                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                                },
                                '&.MuiInputLabel-shrink': {
                                    color: theme.palette.text.primary,
                                }
                            }}
                        >
                            Status
                        </InputLabel>
                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            label="Status"
                            displayEmpty
                            sx={{
                                color: theme.palette.text.primary,
                                '& .MuiOutlinedInput-root': {
                                    color: theme.palette.text.primary,
                                    '& fieldset': {
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.primary.main,
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme.palette.text.primary,
                                    '&.Mui-focused': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                                    },
                                    '&.MuiInputLabel-shrink': {
                                        color: theme.palette.text.primary,
                                    }
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    '& legend': {
                                        color: theme.palette.text.primary,
                                    },
                                    '&.Mui-focused': {
                                        borderColor: isDarkMode ? '#ffffff' : 'rgba(0, 0, 0, 0.23)',
                                        '& legend': {
                                            color: theme.palette.text.primary,
                                        },
                                    },
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: isDarkMode ? '#ffffff' : 'rgba(0, 0, 0, 0.23)',
                                    '& legend': {
                                        color: theme.palette.text.primary,
                                    },
                                },
                                '& .MuiSelect-icon': {
                                    color: theme.palette.text.primary,
                                },
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: isDarkMode ? '#1a1a2e' : theme.palette.background.paper,
                                        '& .MuiMenuItem-root': {
                                            color: theme.palette.text.primary,
                                            '&:hover': {
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.palette.action.focus,
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            <MenuItem value="">All Status</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="in_progress">In Progress</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl sx={{
                        minWidth: { xs: 'auto', sm: '100px', md: '120px', lg: '120px' },
                        flex: { xs: 'none', sm: '1', md: '1', lg: '1' },
                        width: { xs: '100%', sm: 'auto' }
                    }}>
                        <InputLabel
                            shrink={true}
                            sx={{
                                color: theme.palette.text.primary,
                                '&.Mui-focused': {
                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                                },
                                '&.MuiInputLabel-shrink': {
                                    color: theme.palette.text.primary,
                                }
                            }}
                        >
                            Mode
                        </InputLabel>
                        <Select
                            value={modeFilter}
                            onChange={(e) => setModeFilter(e.target.value)}
                            label="Mode"
                            displayEmpty
                            sx={{
                                color: theme.palette.text.primary,
                                '& .MuiOutlinedInput-root': {
                                    color: theme.palette.text.primary,
                                    '& fieldset': {
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.primary.main,
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme.palette.text.primary,
                                    '&.Mui-focused': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                                    },
                                    '&.MuiInputLabel-shrink': {
                                        color: theme.palette.text.primary,
                                    }
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    '& legend': {
                                        color: theme.palette.text.primary,
                                    },
                                    '&.Mui-focused': {
                                        borderColor: isDarkMode ? '#ffffff' : 'rgba(0, 0, 0, 0.23)',
                                        '& legend': {
                                            color: theme.palette.text.primary,
                                        },
                                    },
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: isDarkMode ? '#ffffff' : 'rgba(0, 0, 0, 0.23)',
                                    '& legend': {
                                        color: theme.palette.text.primary,
                                    },
                                },
                                '& .MuiSelect-icon': {
                                    color: theme.palette.text.primary,
                                },
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: isDarkMode ? '#1a1a2e' : theme.palette.background.paper,
                                        '& .MuiMenuItem-root': {
                                            color: theme.palette.text.primary,
                                            '&:hover': {
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.palette.action.focus,
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            <MenuItem value="">All Modes</MenuItem>
                            <MenuItem value="Online">Online</MenuItem>
                            <MenuItem value="In-person">In-person</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl sx={{
                        minWidth: { xs: 'auto', sm: '100px', md: '120px', lg: '120px' },
                        flex: { xs: 'none', sm: '1', md: '1', lg: '1' },
                        width: { xs: '100%', sm: 'auto' }
                    }}>
                        <InputLabel
                            shrink={true}
                            sx={{
                                color: theme.palette.text.primary,
                                '&.Mui-focused': {
                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                                },
                                '&.MuiInputLabel-shrink': {
                                    color: theme.palette.text.primary,
                                }
                            }}
                        >
                            Assigned To
                        </InputLabel>
                        <Select
                            value={assignedToFilter}
                            onChange={(e) => setAssignedToFilter(e.target.value)}
                            label="Assigned To"
                            displayEmpty
                            sx={{
                                color: theme.palette.text.primary,
                                '& .MuiOutlinedInput-root': {
                                    color: theme.palette.text.primary,
                                    '& fieldset': {
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.primary.main,
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme.palette.text.primary,
                                    '&.Mui-focused': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                                    },
                                    '&.MuiInputLabel-shrink': {
                                        color: theme.palette.text.primary,
                                    }
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    '& legend': {
                                        color: theme.palette.text.primary,
                                    },
                                    '&.Mui-focused': {
                                        borderColor: isDarkMode ? '#ffffff' : 'rgba(0, 0, 0, 0.23)',
                                        '& legend': {
                                            color: theme.palette.text.primary,
                                        },
                                    },
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: isDarkMode ? '#ffffff' : 'rgba(0, 0, 0, 0.23)',
                                    '& legend': {
                                        color: theme.palette.text.primary,
                                    },
                                },
                                '& .MuiSelect-icon': {
                                    color: theme.palette.text.primary,
                                },
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: isDarkMode ? '#1a1a2e' : theme.palette.background.paper,
                                        '& .MuiMenuItem-root': {
                                            color: theme.palette.text.primary,
                                            '&:hover': {
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.palette.action.focus,
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            <MenuItem value="">All Users</MenuItem>
                            {users.map((user) => (
                                <MenuItem key={user.id} value={user.name}>
                                    {user.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenAddDialog(true)}
                    sx={{
                        backgroundColor: '#2196F3',
                        minWidth: { xs: '100%', lg: 'auto' },
                        height: { xs: '48px', sm: '56px' },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        px: 3,
                        flex: '0 0 auto',
                        '&:hover': {
                            backgroundColor: '#1976D2',
                        },
                        '&:active': {
                            transform: 'scale(0.98)',
                            transition: 'transform 0.1s ease',
                        },
                        // Touch-friendly interactions
                        '@media (hover: none) and (pointer: coarse)': {
                            '&:hover': {
                                backgroundColor: '#2196F3',
                            },
                            '&:active': {
                                backgroundColor: '#1976D2',
                                transform: 'scale(0.95)',
                            },
                        },
                    }}
                >
                    Add Checklist
                </Button>

                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                        console.log('🔄 Refresh button clicked - reloading page');
                        window.location.reload();
                    }}
                    sx={{
                        color: theme.palette.text.secondary,
                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)',
                        height: { xs: '48px', sm: '56px' },
                        width: { xs: '100%', lg: 'auto' },
                        minWidth: { xs: 'auto', lg: '120px' },
                        px: 3,
                        flex: '0 0 auto',
                        fontSize: '1rem',
                        '&:hover': {
                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                        },
                        '&:active': {
                            transform: 'scale(0.98)',
                            transition: 'transform 0.1s ease',
                        }
                    }}
                >
                    Refresh
                </Button>
            </Box>

            {/* Mobile/Tablet Card Layout */}
            <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                {loading ? (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 6,
                        gap: 2
                    }}>
                        <CircularProgress
                            size={40}
                            sx={{
                                color: theme.palette.primary.main,
                                mb: 1
                            }}
                        />
                        <Typography variant="body1" sx={{
                            color: theme.palette.text.primary,
                            textAlign: 'center'
                        }}>
                            Loading tasks...
                        </Typography>
                        <Typography variant="body2" sx={{
                            color: theme.palette.text.secondary,
                            textAlign: 'center'
                        }}>
                            Please wait while we fetch your data
                        </Typography>
                    </Box>
                ) : filteredTasks.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2 }}>
                            No tasks found
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Try adjusting your filters or check back later.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{
                        px: { xs: 1, sm: 2 },
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                    }}>
                        {filteredTasks.map((task, index) => (
                            <TaskCard key={`mobile-task-${task.id}-${index}`} task={task} />
                        ))}
                    </Box>
                )}
            </Box>

            {/* Desktop Table Layout */}
            <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                <Card sx={{
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
                    backdropFilter: 'blur(10px)',
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    boxSizing: 'border-box'
                }}>
                    <TableContainer
                        sx={{
                            overflow: 'auto',
                            maxHeight: { xs: '70vh', sm: 'none' },
                            width: '100%',
                            maxWidth: '100%',
                            // Prevent horizontal scroll issues
                            '&::-webkit-scrollbar': {
                                height: '8px',
                                width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: isDarkMode
                                    ? 'rgba(255, 255, 255, 0.1)'
                                    : 'rgba(0, 0, 0, 0.05)',
                                borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: isDarkMode
                                    ? 'rgba(255, 255, 255, 0.3)'
                                    : 'rgba(0, 0, 0, 0.2)',
                                borderRadius: '4px',
                                '&:hover': {
                                    background: isDarkMode
                                        ? 'rgba(255, 255, 255, 0.5)'
                                        : 'rgba(0, 0, 0, 0.3)',
                                },
                            },
                        }}
                    >
                        <Table sx={{
                            width: '100%',
                            minWidth: { xs: '100%', sm: '100%', md: '100%', lg: '100%' },
                            tableLayout: 'auto',
                            '& .MuiTableCell-root': {
                                padding: {
                                    xs: '8px 4px',
                                    sm: '6px 4px',
                                    md: '8px 6px',
                                    lg: '10px 8px',
                                    xl: '12px 10px'
                                },
                                fontSize: {
                                    xs: '0.7rem',
                                    sm: '0.65rem',
                                    md: '0.7rem',
                                    lg: '0.75rem',
                                    xl: '0.8rem'
                                },
                                whiteSpace: { xs: 'normal', sm: 'normal', md: 'nowrap' },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: { xs: 1.2, sm: 1.2, md: 1.3, lg: 1.4 },
                                borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
                            }
                        }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{
                                        width: { xs: '25%', sm: '20%', md: '18%', lg: '16%' },
                                        minWidth: { xs: '120px', sm: '140px', md: '160px', lg: '180px' },
                                        display: { xs: 'table-cell', sm: 'table-cell', md: 'table-cell', lg: 'table-cell' }
                                    }}>Task Name</TableCell>
                                    <TableCell sx={{
                                        width: { xs: '0%', sm: '15%', md: '14%', lg: '13%' },
                                        minWidth: { xs: '0px', sm: '100px', md: '120px', lg: '140px' },
                                        display: { xs: 'none', sm: 'table-cell', md: 'table-cell', lg: 'table-cell' }
                                    }}>Tools/Resources</TableCell>
                                    <TableCell sx={{
                                        width: { xs: '15%', sm: '12%', md: '10%', lg: '8%' },
                                        minWidth: { xs: '60px', sm: '70px', md: '80px', lg: '90px' },
                                        display: { xs: 'table-cell', sm: 'table-cell', md: 'table-cell', lg: 'table-cell' }
                                    }}>Mode</TableCell>
                                    <TableCell sx={{
                                        width: { xs: '0%', sm: '0%', md: '15%', lg: '14%' },
                                        minWidth: { xs: '0px', sm: '0px', md: '120px', lg: '140px' },
                                        display: { xs: 'none', sm: 'none', md: 'table-cell', lg: 'table-cell' }
                                    }}>Key Deliverable</TableCell>
                                    <TableCell sx={{
                                        width: { xs: '15%', sm: '12%', md: '10%', lg: '8%' },
                                        minWidth: { xs: '60px', sm: '70px', md: '80px', lg: '90px' },
                                        display: { xs: 'table-cell', sm: 'table-cell', md: 'table-cell', lg: 'table-cell' }
                                    }}>Assigned To</TableCell>
                                    <TableCell sx={{
                                        width: { xs: '15%', sm: '12%', md: '10%', lg: '8%' },
                                        minWidth: { xs: '60px', sm: '70px', md: '80px', lg: '90px' },
                                        display: { xs: 'table-cell', sm: 'table-cell', md: 'table-cell', lg: 'table-cell' }
                                    }}>Schedule Date</TableCell>
                                    <TableCell sx={{
                                        width: { xs: '0%', sm: '0%', md: '10%', lg: '8%' },
                                        minWidth: { xs: '0px', sm: '0px', md: '80px', lg: '90px' },
                                        display: { xs: 'none', sm: 'none', md: 'table-cell', lg: 'table-cell' }
                                    }}>Completion Date</TableCell>
                                    <TableCell sx={{
                                        width: { xs: '0%', sm: '0%', md: '0%', lg: '8%' },
                                        minWidth: { xs: '0px', sm: '0px', md: '0px', lg: '80px' },
                                        display: { xs: 'none', sm: 'none', md: 'none', lg: 'table-cell' }
                                    }}>Created At</TableCell>
                                    <TableCell sx={{
                                        width: { xs: '0%', sm: '0%', md: '0%', lg: '8%' },
                                        minWidth: { xs: '0px', sm: '0px', md: '0px', lg: '80px' },
                                        display: { xs: 'none', sm: 'none', md: 'none', lg: 'table-cell' }
                                    }}>Updated At</TableCell>
                                    <TableCell sx={{
                                        width: { xs: '0%', sm: '0%', md: '0%', lg: '8%' },
                                        minWidth: { xs: '0px', sm: '0px', md: '0px', lg: '80px' },
                                        display: { xs: 'none', sm: 'none', md: 'none', lg: 'table-cell' }
                                    }}>Notes</TableCell>
                                    <TableCell sx={{
                                        width: { xs: '15%', sm: '12%', md: '10%', lg: '8%' },
                                        minWidth: { xs: '60px', sm: '70px', md: '80px', lg: '90px' },
                                        display: { xs: 'table-cell', sm: 'table-cell', md: 'table-cell', lg: 'table-cell' }
                                    }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={isDesktop ? 11 : 6} sx={{ textAlign: 'center', py: 6 }}>
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 2
                                            }}>
                                                <CircularProgress
                                                    size={40}
                                                    sx={{
                                                        color: theme.palette.primary.main,
                                                        mb: 1
                                                    }}
                                                />
                                                <Typography variant="body1" sx={{
                                                    color: theme.palette.text.primary,
                                                    textAlign: 'center'
                                                }}>
                                                    Loading tasks...
                                                </Typography>
                                                <Typography variant="body2" sx={{
                                                    color: theme.palette.text.secondary,
                                                    textAlign: 'center'
                                                }}>
                                                    Please wait while we fetch your data
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredTasks.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={isDesktop ? 11 : 6} sx={{ textAlign: 'center', color: theme.palette.text.primary, py: 4 }}>
                                            No tasks found for this program.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredTasks.map((task, index) => (
                                        <TableRow
                                            key={`task-${task.id}-${index}`}
                                            hover
                                            sx={{ '&:hover': { backgroundColor: theme.palette.action.hover } }}
                                        >
                                            <TableCell sx={{
                                                width: { xs: '25%', sm: '20%', md: '18%', lg: '16%' },
                                                minWidth: { xs: '120px', sm: '140px', md: '160px', lg: '180px' },
                                                display: { xs: 'table-cell', sm: 'table-cell', md: 'table-cell', lg: 'table-cell' }
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                    <Checkbox
                                                        checked={task.status === 'completed'}
                                                        indeterminate={task.status === 'in_progress'}
                                                        onChange={() => handleTaskStatusChange(task.id.toString())}
                                                        sx={{ ...checkboxStyles, mt: 0.5 }}
                                                    />
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: theme.palette.text.primary,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'normal',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.2,
                                                                mb: 0.5
                                                            }}
                                                        >
                                                            {task.taskName || '-'}
                                                        </Typography>
                                                        {task.status === 'in_progress' && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: statusColors.in_progress || '#ff9800',
                                                                    display: 'block',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '0.7rem',
                                                                    lineHeight: 1,
                                                                    mt: 0.5
                                                                }}
                                                            >
                                                                🔄 In Progress
                                                            </Typography>
                                                        )}
                                                        {task.status === 'completed' && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: statusColors.completed || 'rgba(76, 175, 80, 0.8)',
                                                                    display: 'block',
                                                                    fontSize: '0.7rem',
                                                                    lineHeight: 1,
                                                                    mt: 0.5
                                                                }}
                                                            >
                                                                ✅ Completed
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: '15%',
                                                minWidth: '150px',
                                                maxWidth: '200px'
                                            }}>
                                                <Typography variant="body2" sx={{
                                                    color: theme.palette.text.secondary,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    fontSize: { lg: '0.875rem' }
                                                }}>
                                                    {task.tools_resources || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: '10%',
                                                minWidth: '80px',
                                                maxWidth: '100px'
                                            }}>
                                                <Typography variant="body2" sx={{
                                                    color: theme.palette.text.secondary,
                                                    fontSize: { lg: '0.875rem' },
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word'
                                                }}>
                                                    {task.mode || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: { xs: '0%', sm: '15%', md: '14%', lg: '13%' },
                                                minWidth: { xs: '0px', sm: '100px', md: '120px', lg: '140px' },
                                                display: { xs: 'none', sm: 'table-cell', md: 'table-cell', lg: 'table-cell' }
                                            }}>
                                                <Typography variant="body2" sx={{
                                                    color: theme.palette.text.primary,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    fontSize: { lg: '0.875rem' }
                                                }}>
                                                    {task.key_deliverable || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: '12%',
                                                minWidth: '100px',
                                                maxWidth: '140px'
                                            }}>
                                                <Typography variant="body2" sx={{
                                                    color: theme.palette.text.primary,
                                                    fontSize: { md: '0.7rem', lg: '0.8rem' },
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word'
                                                }}>
                                                    {task.assigned_to}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: '10%',
                                                minWidth: '100px',
                                                maxWidth: '140px'
                                            }}>
                                                <Typography variant="body2" sx={{
                                                    fontSize: { md: '0.7rem', lg: '0.8rem' },
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {formatTableDate(task.due_date)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: '10%',
                                                minWidth: '100px',
                                                maxWidth: '140px'
                                            }}>
                                                <Typography variant="body2" sx={{
                                                    fontSize: { md: '0.7rem', lg: '0.8rem' },
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'normal'
                                                }}>
                                                    {formatTableDate(task.completion_date)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: '8%',
                                                minWidth: '80px',
                                                maxWidth: '120px'
                                            }}>
                                                <Typography variant="body2" sx={{
                                                    fontSize: { md: '0.75rem', lg: '0.875rem' }
                                                }}>
                                                    {formatTableDate(task.created_at)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: '8%',
                                                minWidth: '80px',
                                                maxWidth: '120px'
                                            }}>
                                                <Typography variant="body2" sx={{
                                                    fontSize: { md: '0.75rem', lg: '0.875rem' }
                                                }}>
                                                    {formatTableDate(task.updated_at)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: '12%',
                                                minWidth: '120px',
                                                maxWidth: '200px'
                                            }}>
                                                <Typography variant="body2" sx={{
                                                    color: theme.palette.text.secondary,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    fontSize: { md: '0.75rem', lg: '0.875rem' }
                                                }}>
                                                    {task.notes || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{
                                                width: '10%',
                                                minWidth: '100px',
                                                maxWidth: '120px',
                                                position: 'sticky',
                                                right: 0,
                                                backgroundColor: isDarkMode ? 'rgba(26, 26, 46, 0.95)' : 'rgba(250, 250, 250, 0.95)',
                                                zIndex: 20,
                                                borderLeft: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.12)'}`,
                                                padding: { xs: '6px 8px', sm: '8px 10px', md: '10px 12px', lg: '12px 16px' },
                                                verticalAlign: 'middle'
                                            }}>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    sx={{
                                                        justifyContent: 'center',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <Tooltip title={isPredefinedTask(task.checklist_id, 2) ? "Cannot edit predefined task" : "Edit Task"}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={isPredefinedTask(task.checklist_id, 2)}
                                                                onClick={() => handleEditTask(task)}
                                                                sx={editButtonStyles}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={isPredefinedTask(task.checklist_id, 2) ? "Cannot delete predefined task" : "Delete Task"}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={isPredefinedTask(task.checklist_id, 2)}
                                                                onClick={() => handleDeleteTask(task)}
                                                                sx={deleteButtonStyles}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>

            {/* Tasks Cards - Mobile */}
            <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                <Box display="flex" flexDirection="column" gap={2}>
                    {filteredTasks.map((task) => (
                        <Card key={task.id} sx={{
                            background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
                            backdropFilter: 'blur(10px)',
                            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            p: 2,
                        }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                <Box flex={1}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Checkbox
                                            checked={task.status === 'completed'}
                                            indeterminate={task.status === 'in_progress'}
                                            onChange={() => handleTaskStatusChange(task.id.toString())}
                                            sx={checkboxStyles}
                                        />
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: 'bold',
                                                textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                                                opacity: task.status === 'completed' ? 0.7 : 1,
                                            }}
                                        >
                                            {task.taskName}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Box display="flex" flexDirection="column" gap={1} mb={2}>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        Status:
                                    </Typography>
                                    <Chip
                                        label={task.status === 'completed' ? 'Completed' :
                                            task.status === 'in_progress' ? 'In Progress' : 'Pending'}
                                        size="small"
                                        sx={{
                                            backgroundColor: task.status === 'completed' ? '#4CAF50' :
                                                task.status === 'in_progress' ? '#FF9800' : '#9E9E9E',
                                            color: theme.palette.text.primary,
                                            fontWeight: 'bold'
                                        }}
                                    />
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        Mode:
                                    </Typography>
                                    <Chip
                                        label={task.mode}
                                        size="small"
                                        sx={{
                                            backgroundColor: task.mode === 'Online' ? '#4CAF50' :
                                                task.mode === 'In-person' ? '#FF9800' :
                                                    task.mode === 'Hybrid' ? '#9C27B0' : '#2196F3',
                                            color: theme.palette.text.primary,
                                            fontWeight: 'bold'
                                        }}
                                    />
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        Assigned To:
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: '#2196F3' }}>
                                            {task.assigned_to.charAt(0)}
                                        </Avatar>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                            {task.assigned_to}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        Due Date:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                        {formatTableDate(task.due_date)}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        Completion Date:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                        {formatTableDate(task.completion_date)}
                                    </Typography>
                                </Box>
                                {task.key_deliverable && (
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                            Key Deliverable:
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, textAlign: 'right', maxWidth: '60%' }}>
                                            {task.key_deliverable}
                                        </Typography>
                                    </Box>
                                )}
                                {task.tools_resources && (
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                            Tools/Resources:
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, textAlign: 'right', maxWidth: '60%' }}>
                                            {task.tools_resources}
                                        </Typography>
                                    </Box>
                                )}
                                {task.notes && (
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                            Notes:
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, textAlign: 'right', maxWidth: '60%' }}>
                                            {task.notes}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            <Box display="flex" justifyContent="flex-end" gap={1}>
                                <Tooltip title={isPredefinedTask(task.checklist_id, 2) ? "Cannot edit predefined task" : "Edit Task"}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={isPredefinedTask(task.checklist_id, 2)}
                                            disableRipple
                                            onClick={() => handleEditTask(task)}
                                            style={{
                                                backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.15)',
                                                color: '#FF9800',
                                                width: '36px',
                                                height: '36px',
                                                minWidth: '36px',
                                                minHeight: '36px',
                                                padding: 0,
                                                borderRadius: '8px'
                                            }}
                                            sx={editButtonStyles}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={isPredefinedTask(task.checklist_id, 2) ? "Cannot delete predefined task" : "Delete Task"}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={isPredefinedTask(task.checklist_id, 2)}
                                            disableRipple
                                            onClick={() => handleDeleteTask(task)}
                                            style={{
                                                backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.15)',
                                                color: '#F44336',
                                                width: '36px',
                                                height: '36px',
                                                minWidth: '36px',
                                                minHeight: '36px',
                                                padding: 0,
                                                borderRadius: '8px'
                                            }}
                                            sx={deleteButtonStyles}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Card>
                    ))}
                </Box>

            </Box>

            {/* Export Buttons - Below Table */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'center',
                gap: { xs: 2, sm: 2 },
                mt: 3,
                mb: 3,
                alignItems: 'center'
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 2 },
                    width: { xs: '100%', sm: 'auto' }
                }}>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportCSV}
                        disabled={tasks.length === 0}
                        sx={{
                            backgroundColor: '#4CAF50',
                            width: { xs: '100%', sm: 'auto' },
                            minWidth: { xs: '100%', sm: '120px' },
                            height: { xs: '48px', sm: '40px' },
                            fontSize: { xs: '0.875rem', sm: '0.875rem' },
                            '&:hover': {
                                backgroundColor: '#45a049',
                            },
                            '&:disabled': {
                                backgroundColor: 'rgba(76, 175, 80, 0.3)',
                                color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.text.disabled
                            },
                            '&:active': {
                                transform: 'scale(0.98)',
                                transition: 'transform 0.1s ease',
                            },
                            '@media (hover: none) and (pointer: coarse)': {
                                '&:hover': { backgroundColor: '#4CAF50' },
                                '&:active': {
                                    backgroundColor: '#45a049',
                                    transform: 'scale(0.95)',
                                },
                            },
                        }}
                    >
                        Export CSV
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<PDFIcon />}
                        onClick={handleExportPDF}
                        disabled={tasks.length === 0}
                        sx={{
                            backgroundColor: '#F44336',
                            width: { xs: '100%', sm: 'auto' },
                            minWidth: { xs: '100%', sm: '120px' },
                            height: { xs: '48px', sm: '40px' },
                            fontSize: { xs: '0.875rem', sm: '0.875rem' },
                            '&:hover': {
                                backgroundColor: '#D32F2F',
                            },
                            '&:disabled': {
                                backgroundColor: 'rgba(244, 67, 54, 0.3)',
                                color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.text.disabled
                            },
                            '&:active': {
                                transform: 'scale(0.98)',
                                transition: 'transform 0.1s ease',
                            },
                            '@media (hover: none) and (pointer: coarse)': {
                                '&:hover': { backgroundColor: '#F44336' },
                                '&:active': {
                                    backgroundColor: '#D32F2F',
                                    transform: 'scale(0.95)',
                                },
                            },
                        }}
                    >
                        Export PDF
                    </Button>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<UploadIcon />}
                    onClick={handleImportTasks}
                    sx={{
                        backgroundColor: '#FF9800',
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { xs: '100%', sm: '120px' },
                        height: { xs: '48px', sm: '40px' },
                        fontSize: { xs: '0.875rem', sm: '0.875rem' },
                        '&:hover': {
                            backgroundColor: '#F57C00',
                        },
                        '&:active': {
                            transform: 'scale(0.98)',
                            transition: 'transform 0.1s ease',
                        },
                        '@media (hover: none) and (pointer: coarse)': {
                            '&:hover': { backgroundColor: '#FF9800' },
                            '&:active': {
                                backgroundColor: '#F57C00',
                                transform: 'scale(0.95)',
                            },
                        },
                    }}
                >
                    Import Tasks
                </Button>
            </Box>

            {/* Edit Task Dialog */}
            <Dialog
                open={editTaskDialogOpen}
                onClose={() => setEditTaskDialogOpen(false)}
                maxWidth="md"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: {
                        maxHeight: 'calc(100vh - 120px)',
                        marginTop: '80px',
                        marginBottom: '40px',
                        marginLeft: '32px',
                        marginRight: '32px'
                    }
                }}
            >
                <DialogTitle sx={{
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    fontSize: '1.25rem',
                    fontWeight: 'bold'
                }}>
                    ✏️ Edit Task Details
                </DialogTitle>
                <Box component="form" onSubmit={handleTaskSubmit(handleUpdateTask)}>
                    <DialogContent sx={{
                        backgroundColor: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none'
                    }}>
                        <Typography variant="body2" sx={{
                            color: theme.palette.text.secondary,
                            mb: 2,
                            mt: 2,
                            fontStyle: 'italic'
                        }}>
                            Update the task details below:
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
                            <Controller
                                name="taskName"
                                control={taskControl}
                                rules={{ required: 'Task name is required' }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Task Name"
                                        fullWidth
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        disabled={!!(editingTask && editingTask.checklist_id && parseInt(editingTask.checklist_id.toString()) <= 36)}
                                        error={!!taskErrors.taskName}
                                        helperText={taskErrors.taskName?.message}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.palette.text.primary,
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.23)',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />

                            {/* Row 1: Assigned To */}
                            <Controller
                                name="assignedTo"
                                control={taskControl}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <InputLabel
                                            shrink={true}
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                                                },
                                                '&.MuiInputLabel-shrink': {
                                                    transform: 'translate(14px, -9px) scale(0.75)',
                                                    backgroundColor: isDarkMode ? '#1a1a2e' : theme.palette.background.paper,
                                                    padding: '0 8px',
                                                },
                                            }}
                                        >
                                            Assigned To
                                        </InputLabel>
                                        <Select
                                            {...field}
                                            label="Assigned To"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.23)',
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: theme.palette.text.secondary,
                                                    '&.Mui-focused': {
                                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                                                    },
                                                },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        backgroundColor: isDarkMode ? '#1a1a2e' : theme.palette.background.paper,
                                                        color: theme.palette.text.primary,
                                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                                        '& .MuiMenuItem-root': {
                                                            color: theme.palette.text.primary,
                                                            '&:hover': {
                                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                                            },
                                                            '&.Mui-selected': {
                                                                backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(33, 150, 243, 0.3)',
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            <MenuItem value="">
                                                <em>Select a user</em>
                                            </MenuItem>
                                            {users.map((user) => (
                                                <MenuItem key={user.id} value={user.name}>
                                                    {user.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />

                            {/* Row 2: Due Date */}
                            <Controller
                                name="dueDate"
                                control={taskControl}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Schedule Date"
                                        type="date"
                                        fullWidth
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                                                filter: isDarkMode ? 'invert(1)' : 'invert(0)',
                                                cursor: 'pointer',
                                                opacity: 0.7,
                                                '&:hover': {
                                                    opacity: 1
                                                }
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.palette.text.primary,
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.23)',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                name="completionDate"
                                control={taskControl}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Completion Date"
                                        type="date"
                                        fullWidth
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                                                filter: isDarkMode ? 'invert(1)' : 'invert(0)',
                                                cursor: 'pointer',
                                                opacity: 0.7,
                                                '&:hover': {
                                                    opacity: 1
                                                }
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.palette.text.primary,
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.23)',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />

                            <Controller
                                name="mode"
                                control={taskControl}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <InputLabel
                                            shrink={true}
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                                                },
                                                '&.MuiInputLabel-shrink': {
                                                    transform: 'translate(14px, -9px) scale(0.75)',
                                                    backgroundColor: isDarkMode ? '#1a1a2e' : theme.palette.background.paper,
                                                    padding: '0 8px',
                                                },
                                            }}
                                        >
                                            Mode
                                        </InputLabel>
                                        <Select
                                            {...field}
                                            label="Mode"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.divider,
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.23)',
                                                },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        backgroundColor: isDarkMode ? '#1a1a2e' : theme.palette.background.paper,
                                                        color: theme.palette.text.primary,
                                                        '& .MuiMenuItem-root': {
                                                            color: theme.palette.text.primary,
                                                            '&:hover': {
                                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            <MenuItem value="Online">Online</MenuItem>
                                            <MenuItem value="In-person">In-person</MenuItem>
                                            <MenuItem value="Hybrid">Hybrid</MenuItem>
                                            <MenuItem value="Remote">Remote</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            />

                            {/* Tools/Resources */}
                            <Controller
                                name="toolsResources"
                                control={taskControl}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Tools/Resources"
                                        fullWidth
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.palette.text.primary,
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.23)',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />

                            {/* Notes */}
                            <Controller
                                name="notes"
                                control={taskControl}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Notes"
                                        multiline
                                        rows={3}
                                        fullWidth
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.palette.text.primary,
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.23)',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{
                        backgroundColor: theme.palette.background.default,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        padding: '16px 24px'
                    }}>
                        <Button
                            onClick={() => setEditTaskDialogOpen(false)}
                            variant="outlined"
                            sx={{
                                color: theme.palette.text.primary,
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)',
                                borderWidth: 1.5,
                                '&:hover': {
                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                    borderWidth: 1.5
                                },
                                '&:focus': {
                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                    outline: 'none'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            onClick={(e) => {
                                console.log('🖱️ Workshop Update button clicked!');
                                console.log('🖱️ Workshop editingTask:', editingTask);
                                console.log('🖱️ Workshop selectedProgram:', selectedProgram);
                                console.log('🖱️ Workshop Form validation errors:', taskErrors);
                            }}
                            sx={{
                                backgroundColor: '#2196F3',
                                color: '#ffffff',
                                fontWeight: 500,
                                '&:hover': {
                                    backgroundColor: '#1976D2'
                                },
                                '&:focus': {
                                    outline: 'none'
                                }
                            }}
                        >
                            Update
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                    Delete Task
                </DialogTitle>
                <DialogContent sx={{
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary
                }}>
                    <Typography sx={{ mt: 2 }}>
                        Are you sure you want to delete "{taskToDelete?.taskName}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{
                    backgroundColor: theme.palette.background.default,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    padding: '16px 24px'
                }}>
                    <Button
                        onClick={() => {
                            setDeleteDialogOpen(false);
                            setTaskToDelete(null);
                        }}
                        variant="outlined"
                        sx={{
                            color: theme.palette.text.primary,
                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)',
                            borderWidth: 1.5,
                            '&:hover': {
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                borderWidth: 1.5
                            },
                            '&:focus': {
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                outline: 'none'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Checklist Dialog */}
            {openAddDialog && (
                <Dialog
                    open={openAddDialog}
                    onClose={handleAddDialogClose}
                    maxWidth="sm"
                    fullWidth
                    scroll="paper"
                    PaperProps={{
                        sx: {
                            maxHeight: 'calc(100vh - 120px)',
                            marginTop: '80px',
                            marginBottom: '40px',
                            marginLeft: '32px',
                            marginRight: '32px'
                        }
                    }}
                >
                    <DialogTitle sx={{
                        backgroundColor: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        fontSize: '1.25rem',
                        fontWeight: 'bold'
                    }}>
                        ➕ Add New Task
                    </DialogTitle>
                    <Box component="form" onSubmit={handleAddChecklistSubmit(handleAddChecklist)}>
                        <DialogContent sx={{
                            backgroundColor: theme.palette.background.default,
                            color: theme.palette.text.primary,
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                                display: 'none'
                            },
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            // Global styles for all form fields in dark mode
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                                },
                                '&:hover fieldset': {
                                    borderColor: isDarkMode ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: isDarkMode ? '#ffffff' : 'rgba(0, 0, 0, 0.6)',
                                '&.Mui-focused': {
                                    color: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                },
                            },
                            // Calendar icon styling for date inputs
                            '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                filter: isDarkMode ? 'invert(1)' : 'invert(0)',
                                cursor: 'pointer',
                            },
                        }}>
                            <Typography variant="body2" sx={{
                                color: theme.palette.text.secondary,
                                mb: 2,
                                mt: 2,
                                fontStyle: 'italic'
                            }}>
                                Fill in the details to create a new task.
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
                                <Controller
                                    name="taskName"
                                    control={addChecklistControl}
                                    rules={{ required: 'Task name is required' }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Task Name"
                                            placeholder="Enter task name"
                                            required
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                            error={!!addChecklistErrors.taskName || !!addChecklistError}
                                            helperText={addChecklistErrors.taskName?.message || addChecklistError}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    color: theme.palette.text.primary,
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: theme.palette.text.secondary,
                                                    '&.Mui-focused': {
                                                        color: theme.palette.text.primary,
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name="toolsResources"
                                    control={addChecklistControl}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Tools/Resources"
                                            placeholder="Enter tools or resources needed"
                                            multiline
                                            rows={2}
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    color: theme.palette.text.primary,
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: theme.palette.text.secondary,
                                                    '&.Mui-focused': {
                                                        color: theme.palette.text.primary,
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name="mode"
                                    control={addChecklistControl}
                                    render={({ field }) => (
                                        <FormControl fullWidth>
                                            <InputLabel shrink={true} sx={{
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: theme.palette.text.primary,
                                                },
                                            }}>Mode</InputLabel>
                                            <Select
                                                {...field}
                                                label="Mode"
                                                sx={{
                                                    color: theme.palette.text.primary,
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                    },
                                                }}
                                            >
                                                <MenuItem value="Online">Online</MenuItem>
                                                <MenuItem value="In-person">In-person</MenuItem>
                                                <MenuItem value="Hybrid">Hybrid</MenuItem>
                                                <MenuItem value="Remote">Remote</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                />
                                <Controller
                                    name="keyDeliverable"
                                    control={addChecklistControl}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Key Deliverable"
                                            placeholder="Enter key deliverable"
                                            multiline
                                            rows={2}
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    color: theme.palette.text.primary,
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: theme.palette.text.secondary,
                                                    '&.Mui-focused': {
                                                        color: theme.palette.text.primary,
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name="assignedTo"
                                    control={addChecklistControl}
                                    render={({ field }) => (
                                        <FormControl fullWidth>
                                            <InputLabel shrink={true} sx={{
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: theme.palette.text.primary,
                                                },
                                            }}>Assigned To</InputLabel>
                                            <Select
                                                {...field}
                                                label="Assigned To"
                                                sx={{
                                                    color: theme.palette.text.primary,
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                    },
                                                }}
                                            >
                                                <MenuItem value="">None</MenuItem>
                                                {users.map((user) => (
                                                    <MenuItem key={user.id} value={user.name}>
                                                        {user.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                />
                                <Controller
                                    name="scheduleDate"
                                    control={addChecklistControl}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Schedule Date"
                                            type="date"
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                                                    filter: isDarkMode ? 'invert(1)' : 'invert(0)',
                                                    cursor: 'pointer',
                                                    opacity: 0.7,
                                                    '&:hover': {
                                                        opacity: 1,
                                                    },
                                                },
                                                '& .MuiOutlinedInput-root': {
                                                    color: theme.palette.text.primary,
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: theme.palette.text.secondary,
                                                    '&.Mui-focused': {
                                                        color: theme.palette.text.primary,
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name="notes"
                                    control={addChecklistControl}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Notes"
                                            placeholder="Enter any additional notes"
                                            multiline
                                            rows={3}
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    color: theme.palette.text.primary,
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: theme.palette.text.secondary,
                                                    '&.Mui-focused': {
                                                        color: theme.palette.text.primary,
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{
                            backgroundColor: theme.palette.background.default,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            padding: '16px 24px'
                        }}>
                            <Button
                                onClick={handleAddDialogClose}
                                variant="outlined"
                                sx={{
                                    color: theme.palette.text.primary,
                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)',
                                    borderWidth: 1.5,
                                    '&:hover': {
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                        borderWidth: 1.5
                                    },
                                    '&:focus': {
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                        outline: 'none'
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={addChecklistLoading}
                                startIcon={addChecklistLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
                                onClick={(e) => {
                                    console.log('🖱️ Workshop Create button clicked!');
                                    console.log('🖱️ Workshop Button disabled?', addChecklistLoading);
                                    console.log('🖱️ Workshop Form validation errors:', addChecklistErrors);
                                    console.log('🖱️ Workshop Selected program:', selectedProgram);
                                }}
                                sx={{
                                    backgroundColor: '#2196F3',
                                    color: '#ffffff',
                                    fontWeight: 500,
                                    '&:hover': {
                                        backgroundColor: '#1976D2'
                                    },
                                    '&:focus': {
                                        outline: 'none'
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: '#2196F3',
                                        color: '#ffffff',
                                        opacity: 0.4
                                    }
                                }}
                            >
                                {addChecklistLoading ? 'Creating...' : 'Create'}
                            </Button>
                        </DialogActions>
                    </Box>
                </Dialog>
            )}

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default WorkshopTasksPage;
